import { useCallback, useEffect, useState } from 'react'
import { Autocomplete, ListItem, ListItemText, Stack, TextField, Typography } from '@mui/material'
import { debounce } from '@mui/material/utils'
import { AddressDto } from '../../../interfaces/IAssignment'

interface AddressAutocompleteProps {
  selectedLocation?: AddressDto | null
  handleLocationChanged: (address: AddressDto | null) => void
  disabled?: boolean
}

export function AddressAutocomplete({ handleLocationChanged, selectedLocation, disabled = false }: AddressAutocompleteProps) {
  const [geoNorgeAddresses, setGeoNorgeAddresses] = useState<AddressDto[]>([])
  const [locationInput, setLocationInput] = useState('')

  const debouncedSearch = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce((value: string) => {
      fetch(`https://ws.geonorge.no/adresser/v1/sok?sok=${value}*&treffPerSide=10&side=0&asciiKompatibel=true`)
        .then((res) => res.json())
        .then((data) => {
          const addresses: Array<AddressDto> = data.adresser.flatMap((a: any) => {
            const previewLat = a.representasjonspunkt?.lat
            const previewLon = a.representasjonspunkt?.lon
            const hasPreviewCoords = previewLat !== undefined && previewLon !== undefined
            if (a.bruksenhetsnummer.length > 1) {
              return a.bruksenhetsnummer.map((bruksenhetsnummer: any) => ({
                city: a.poststed,
                streetAddress: a.adressetekst,
                propertyUnitNumber: bruksenhetsnummer,
                postalCode: a.postnummer,
                municipality: a.kommunenavn,
                latitude: previewLat,
                longitude: previewLon,
                srid: hasPreviewCoords ? 4326 : undefined,
                addressString: `${a.adressetekst} ${bruksenhetsnummer}, ${a.postnummer} ${a.poststed}`,
              }))
            } else {
              return {
                city: a.poststed,
                streetAddress: a.adressetekst,
                postalCode: a.postnummer,
                propertyUnitNumber: '',
                municipality: a.kommunenavn,
                latitude: previewLat,
                longitude: previewLon,
                srid: hasPreviewCoords ? 4326 : undefined,
                addressString: `${a.adressetekst}, ${a.postnummer} ${a.poststed}`,
              }
            }
          })
          setGeoNorgeAddresses(addresses)
        })
    }, 200),
    [],
  )

  useEffect(() => {
    if (!locationInput || locationInput.length < 3) return
    debouncedSearch(locationInput)
  }, [locationInput, debouncedSearch])

  const getOptionLabel = (option: AddressDto | string) => {
    if (typeof option === 'string') {
      return option
    }
    if (option.streetAddress) {
      return `${option.streetAddress} ${option.propertyUnitNumber ? `, ${option.propertyUnitNumber}, ` : ''}${option?.postalCode} ${option?.city}`
    }
    return ''
  }

  const isOptionEqualToValue = (option: AddressDto, value: AddressDto) => {
    if (!value.streetAddress) {
      return true
    }

    return (
      option.streetAddress === value.streetAddress &&
      option.propertyUnitNumber === value.propertyUnitNumber &&
      option.postalCode === value.postalCode &&
      option.city === value.city
    )
  }

  return (
    <Autocomplete
      id="autocomplete-locations"
      options={geoNorgeAddresses}
      value={selectedLocation}
      disabled={disabled}
      onChange={(event, newValue) => {
        if (newValue && typeof newValue !== 'string') {
          handleLocationChanged(newValue)
        } else {
          handleLocationChanged(null)
        }

        handleLocationChanged(newValue)
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={() => {
        return geoNorgeAddresses
      }}
      onInputChange={(event, newInputValue) => {
        setLocationInput(newInputValue)
      }}
      renderInput={(params) => (
        <Stack spacing={1}>
          <Typography variant={'body2'} gutterBottom>
            Adresse
          </Typography>
          <TextField {...params} sx={{ m: 0 }} />
        </Stack>
      )}
      renderOption={(props, option) => (
        <ListItem divider {...props}>
          <ListItemText
            primary={`${option.streetAddress} ${option.propertyUnitNumber ? `, ${option.propertyUnitNumber}, ` : ''}${option?.postalCode} ${option?.city}`}
            slotProps={{
              primary: {
                variant: 'caption',
                color: 'textSecondary',
              },

              secondary: {
                variant: 'body1',
                color: 'textPrimary',
              }
            }} />
        </ListItem>
      )}
    />
  );
}
